import React from "react";
import { DateTime } from "luxon";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";

import { useImageFallback } from "/imports/ui/components/shared";

const ONLINE_PAYMENT_LABEL = "Online payment";

function formatAmount(value, currency, withDigits = true) {
  if (value == null || value === "") return "";
  const num = typeof value === "number" ? value : parseFloat(value);
  if (Number.isNaN(num)) return `${(currency || "").toUpperCase()} ${value}`.trim();
  const formatted = num.toLocaleString(undefined, {
    minimumFractionDigits: withDigits ? 2 : 0,
    maximumFractionDigits: withDigits ? 2 : 0
  });
  return `${(currency || "").toUpperCase()} ${formatted}`;
}

// VAT is tax-inclusive: net = gross / (1 + rate/100), tax = gross - net.
function splitInclusiveTax(gross, ratePct) {
  const g = parseFloat(gross) || 0;
  const r = parseFloat(ratePct) || 0;
  if (r <= 0) return { net: g, tax: 0 };
  const net = g / (1 + r / 100);
  return { net, tax: g - net };
}

function paymentStatusText(p) {
  // A captured payment that was auto-refunded (booking cancelled mid-checkout) is
  // stored as CANCELED with refundData — show it as refunded, not just canceled.
  if (p.refundData) return "Payment refunded";
  if (p.status === "PAID" && p.paidAt) {
    return DateTime.fromISO(p.paidAt).toFormat("ccc, dd LLL yyyy · HH:mm");
  }
  if (p.status === "PAID") return "Payment received";
  if (p.status === "PENDING") return "Payment is pending";
  if (p.status === "CANCELED") return "Payment is canceled";
  if (p.status === "FAILED") return "Payment is failed";
  if (p.status === "ERROR") return "Payment has error";
  return "";
}

function paymentStatusColor(status, isCurrent) {
  if (status === "PAID") return "success.main";
  if (status === "PENDING") return isCurrent ? "success.main" : "text.secondary";
  if (status === "CANCELED" || status === "FAILED" || status === "ERROR") return "error.main";
  return "text.secondary";
}

const PANE_SX = {
  flex: 1,
  minWidth: 0,
  p: { xs: 2.5, sm: 4 }
};

// `leftPaneContent` sits under the booking details on desktop only, where the left
// column otherwise ends in empty space; on a phone the columns stack and the right
// pane carries everything in reading order, so the caller renders it there instead.
export default function CheckoutTelrCard({ data, rightPaneContent, leftPaneContent = null }) {
  const saas = data?.saas || {};
  const { showImage: showLogo, onImageError: onLogoError } = useImageFallback(saas?.logoUrl);
  const invoice = data?.invoice || {};
  const payment = data?.payment || {};
  const charge = data?.charge || {};
  const client = data?.client || {};
  const payments = data?.payments || [];
  const booking = data?.bookingDetails || null;
  const currency = saas?.currency || "AED";

  const items = [...(invoice?.bookishs || []), ...(invoice?.sales || [])];
  // The rich booking block only describes the first bookish, so keep it for a
  // single-line invoice and fall back to the full item list for anything with
  // more (consolidated multi-booking invoices, products, mixed carts).
  const singleBooking = !!booking && items.length <= 1;
  const invoiceTotal = parseFloat(invoice?.total) || parseFloat(payment?.amount) || 0;
  const taxRate = parseFloat(saas?.taxValue) || 0;
  const { net: subtotal, tax } = splitInclusiveTax(invoiceTotal, taxRate);

  const fullName = [client?.firstName, client?.lastName].filter(Boolean).join(" ").trim();

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f3f3f5", py: { xs: 3, sm: 6 } }}>
      <Box sx={{ maxWidth: "68.75rem", mx: "auto", px: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            backgroundColor: "#ffffff",
            borderRadius: 2,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            overflow: "hidden"
          }}
        >
          <Box sx={PANE_SX}>
            {(saas?.logoUrl || saas?.name) && (
              <Box mb={3} display="flex" alignItems="center" gap={1.5}>
                {showLogo ? (
                  <Box
                    component="img"
                    src={saas.logoUrl}
                    alt={saas.name || "Logo"}
                    onError={onLogoError}
                    sx={{ width: "2.5rem", height: "2.5rem", borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: "2.5rem",
                      height: "2.5rem",
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      fontSize: "1.125rem",
                      flexShrink: 0
                    }}
                  >
                    {(saas.name || "?").charAt(0).toUpperCase()}
                  </Box>
                )}
                {saas?.name && (
                  <Typography variant="subtitle1" fontWeight={600}>
                    {saas.name}
                  </Typography>
                )}
              </Box>
            )}

            {singleBooking && (booking.startDate || booking.serviceName) && (
              <Box mb={1}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1.5, textTransform: "uppercase", letterSpacing: 0.8 }}
                >
                  Booking details
                </Typography>
                {booking.serviceName && (
                  <Typography variant="body2" fontWeight={600} mb={0.5}>
                    {booking.serviceName}
                  </Typography>
                )}
                {(booking.facilityName || booking.trainerName || booking.locationName) && (
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    {[booking.facilityName && `on ${booking.facilityName}`, booking.trainerName && `with trainer ${booking.trainerName}`]
                      .filter(Boolean)
                      .join(" ")}
                    {booking.locationName ? (
                      <>
                        {booking.facilityName || booking.trainerName ? ", " : ""}
                        <Box component="span" sx={{ fontWeight: 700, color: "text.primary", fontSize: "1rem" }}>
                          {booking.locationName}
                        </Box>
                      </>
                    ) : null}
                  </Typography>
                )}
                {booking.startDate && (
                  <Typography variant="body2" color="primary.main" mb={0.25}>
                    {DateTime.fromISO(booking.startDate, { zone: "UTC" }).toFormat("ccc, d LLL yyyy")}
                  </Typography>
                )}
                {booking.startDate && booking.endDate && (
                  <Typography variant="body2" color="text.secondary">
                    {DateTime.fromISO(booking.startDate, { zone: "UTC" }).toFormat("HH:mm")}
                    {" – "}
                    {DateTime.fromISO(booking.endDate, { zone: "UTC" }).toFormat("HH:mm")}
                  </Typography>
                )}
              </Box>
            )}

            {items.length > 0 && !singleBooking && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, textTransform: "uppercase", letterSpacing: 0.8 }}>
                  Items
                </Typography>
                {items.map((item, i) => {
                  const qty = parseInt(item?.quantity, 10) || 1;
                  const subtitle = [item?.info, item?.isProduct && qty > 1 ? `${qty} items` : null].filter(Boolean).join(", ");
                  return (
                    <Box key={item?._id || i} display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} py={0.75}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {item?.name}
                        </Typography>
                        {subtitle && (
                          <Typography variant="caption" color="text.secondary">
                            {subtitle}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: "nowrap" }}>
                        {formatAmount(item?.price, currency)}
                      </Typography>
                    </Box>
                  );
                })}
              </>
            )}

            <Divider sx={{ my: 1.5 }} />

            {taxRate > 0 && (
              <>
                <Box display="flex" justifyContent="space-between" py={0.5}>
                  <Typography variant="body2" fontWeight={600}>
                    Subtotal
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatAmount(subtotal, currency)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" py={0.5}>
                  <Typography variant="body2" fontWeight={600}>
                    Tax rate {taxRate}%
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatAmount(tax, currency)}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1.5 }} />
              </>
            )}

            <Box display="flex" justifyContent="space-between" py={0.5}>
              <Typography variant="subtitle1" fontWeight={700}>
                Total
              </Typography>
              <Typography variant="subtitle1" fontWeight={700}>
                {formatAmount(invoiceTotal, currency)}
              </Typography>
            </Box>

            {payments
              .filter((p) => p.payMethod === "PAY_BY_LINK" && parseFloat(p.amount) > 0)
              .map((p) => {
                // A single full payment repeats the Total, so show the status only;
                // a partial deposit genuinely differs, so keep its amount.
                const isFullPayment = Math.abs(parseFloat(p.amount) - invoiceTotal) < 0.005;
                return (
                  <Box key={p._id} sx={{ mt: 2 }}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" fontWeight={600}>
                        {ONLINE_PAYMENT_LABEL}
                      </Typography>
                      {!isFullPayment && (
                        <Typography variant="body2" fontWeight={600}>
                          {formatAmount(p.amount, p.currency || currency)}
                        </Typography>
                      )}
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 0.25,
                        color: p.refundData ? "text.secondary" : paymentStatusColor(p.status, p._id === charge.paymentId)
                      }}
                    >
                      {paymentStatusText(p)}
                    </Typography>
                  </Box>
                );
              })}

            {leftPaneContent && <Box sx={{ mt: 3, display: { xs: "none", md: "block" } }}>{leftPaneContent}</Box>}
          </Box>

          <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" }, my: 5 }} />
          <Divider sx={{ display: { xs: "block", md: "none" } }} />

          <Box sx={{ ...PANE_SX, display: "flex", flexDirection: "column" }}>
            <Box mb={4}>
              {invoice?.invoiceNumber && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  Invoice #{invoice.invoiceNumber}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, textTransform: "uppercase", letterSpacing: 0.8 }}>
                Billed to
              </Typography>
              {fullName && (
                // A customer name is not the headline of this page — at h5 it
                // dominated the panel on a phone.
                <Typography variant="h5" fontWeight={700} mt={0.5} sx={{ fontSize: { xs: "1.15rem", md: "1.5rem" }, wordBreak: "break-word" }}>
                  {fullName}
                </Typography>
              )}
              {client?.email && (
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  {client.email}
                </Typography>
              )}
            </Box>

            <Box flex={1} display="flex" alignItems="center" justifyContent="center">
              {rightPaneContent}
            </Box>

            {saas?.name && (
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ display: "block", textAlign: "center", textTransform: "uppercase", letterSpacing: 1, mt: 3 }}
              >
                Powered by {saas.name} Payment Portal
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
